'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import cl from 'clsx/lite';
import { forwardRef, useContext, useRef, useId, useEffect } from 'react';
import { useMergeRefs } from '../../utilities/hooks/use-merge-refs/use-merge-refs.js';
import '@digdir/designsystemet-web';
import { Button } from '../button/button.js';
import { Context } from './dialog-trigger-context.js';

/**
 * Dialog component, used to display a dialog dialog.
 *
 * @example with TriggerContext
 * <Dialog.TriggerContext>
 *   <Dialog.Trigger>Open Dialog</Dialog.Trigger>
 *   <Dialog>
 *     <Dialog.Block>
 *       Content
 *     </Dialog.Block>
 *   </Dialog>
 * </Dialog.TriggerContext>
 *
 * @example without TriggerContext
 * const dialogRef = useRef<HTMLDialogElement>(null);
 *
 * ...
 *
 * <Button command="show-modal" commandfor="my-dialog">Open Dialog</Button>
 * <Dialog id="my-dialog">
 *   Content
 * </Dialog>
 */
const Dialog = forwardRef(function Dialog({ asChild, children, className, closeButton = 'Lukk dialogvindu', id, modal = true, onAnimationEnd, onClick, onClose, open, placement = 'center', ...rest }, ref) {
    const { setContext } = useContext(Context);
    const dialogRef = useRef(null); // This local ref is used to make sure the dialog works without a DialogTriggerContext
    const Component = asChild ? Slot : 'dialog';
    const mergedRefs = useMergeRefs([ref, dialogRef]);
    const showProp = modal ? 'showModal' : 'show';
    const autoId = useId();
    const usedId = id ?? autoId;
    // Toggle open based on prop
    useEffect(() => dialogRef.current?.[open ? showProp : 'close'](), [open]);
    // Store context for DialogTrigger to consume, so it can open the dialog when the trigger is clicked
    useEffect(() => setContext?.({ id: usedId, modal }), [usedId, modal]);
    return (jsxs(Component, { className: cl('ds-dialog', className), "data-placement": placement, id: usedId, onClose: (event) => onClose?.(event.nativeEvent), onClick: (event) => {
            onClick?.(event);
            const { currentTarget: dialog, target: el, defaultPrevented } = event;
            const isClose = el?.closest?.('[data-command="close"]');
            if (!defaultPrevented && isClose) {
                dialog.close();
                console.warn('Designsystemet: data-command="close" is deprecated. Use command="close" and commandfor="DIALOG-ID" instead.');
            }
        }, onAnimationEnd: (event) => {
            const { currentTarget: dialog } = event;
            const autofocus = dialog.querySelector('[autofocus]');
            if (document.activeElement !== autofocus)
                autofocus?.focus(); // Handle autofocus on open
            onAnimationEnd?.(event);
        }, ref: mergedRefs, ...rest, children: [closeButton !== false && (jsx(Button, { "aria-label": closeButton, "data-color": 'neutral', icon: true, variant: 'tertiary', command: 'close', commandfor: id ?? autoId })), children] }));
});

export { Dialog };
